import { useState } from "react";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { X, Plus, Trash2, RotateCcw, Save, Loader2 } from "lucide-react";
import { MENU, DEFAULT_GROUPS } from "./menuConfig";

// Admin-only sidebar menu editor: create groups, assign pages, hide pages — no code needed.
export const MenuEditor = ({ config, onClose, onSaved }) => {
  const initGroups = (config?.groups?.length ? config.groups : DEFAULT_GROUPS).map((g) => ({ ...g, items: [...g.items] }));
  const [groups, setGroups] = useState(initGroups);
  const [hidden, setHidden] = useState(config?.hidden || []);
  const [newLabel, setNewLabel] = useState("");
  const [saving, setSaving] = useState(false);

  const assignmentOf = (to) => {
    if (hidden.includes(to)) return "__hidden__";
    const g = groups.find((g) => g.items.includes(to));
    return g ? g.id : "__none__";
  };

  const assign = (to, value) => {
    setGroups((gs) => gs.map((g) => ({ ...g, items: g.items.filter((i) => i !== to) })));
    setHidden((h) => h.filter((i) => i !== to));
    if (value === "__hidden__") setHidden((h) => [...h, to]);
    else if (value !== "__none__") setGroups((gs) => gs.map((g) => (g.id === value ? { ...g, items: [...g.items, to] } : g)));
  };

  const addGroup = () => {
    const label = newLabel.trim();
    if (!label) return;
    const id = "g_" + Date.now();
    setGroups((gs) => [...gs, { id, label, items: [] }]);
    setNewLabel("");
  };

  const removeGroup = (id) => setGroups((gs) => gs.filter((g) => g.id !== id));
  const renameGroup = (id, label) => setGroups((gs) => gs.map((g) => (g.id === id ? { ...g, label } : g)));
  const moveGroup = (idx, dir) => {
    setGroups((gs) => {
      const arr = [...gs];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return arr;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return arr;
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings/menu-config", { groups, hidden });
      toast.success("Menü düzeni kaydedildi");
      onSaved();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-2xl rounded-md shadow-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="menu-editor-modal">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5] sticky top-0 bg-white z-10">
          <div>
            <div className="font-heading font-bold text-base">Menü Düzenleyici</div>
            <div className="text-[11px] text-neutral-500 mt-0.5">Sayfaları gruplayın, sıralayın veya gizleyin. Değişiklik tüm kullanıcılara yansır.</div>
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[#F5F5F5]" data-testid="menu-editor-close"><X className="w-4 h-4" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Groups */}
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-600 mb-2">Gruplar</div>
            <div className="space-y-1.5">
              {groups.map((g, idx) => (
                <div key={g.id} className="flex items-center gap-2 bg-[#FAFAFA] border border-[#F5F5F5] px-3 py-2" data-testid={`menu-group-row-${g.id}`}>
                  <div className="flex flex-col gap-0.5">
                    <button onClick={() => moveGroup(idx, -1)} disabled={idx === 0} className="text-[9px] leading-none px-1 disabled:opacity-20 hover:text-[#0020C2]">▲</button>
                    <button onClick={() => moveGroup(idx, 1)} disabled={idx === groups.length - 1} className="text-[9px] leading-none px-1 disabled:opacity-20 hover:text-[#0020C2]">▼</button>
                  </div>
                  <input value={g.label} onChange={(e) => renameGroup(g.id, e.target.value)} className="flex-1 bg-white border border-[#E5E5E5] px-2 py-1 text-sm" />
                  <span className="text-[10px] text-neutral-400 num shrink-0">{g.items.length} sayfa</span>
                  <button onClick={() => removeGroup(g.id)} className="p-1 hover:bg-[#DC2626] hover:text-white shrink-0" data-testid={`menu-group-delete-${g.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <input data-testid="menu-new-group-input" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addGroup()} placeholder="Yeni grup adı (örn: İnsan Kaynakları)" className="flex-1 border border-[#E5E5E5] px-2.5 py-1.5 text-sm" />
              <button data-testid="menu-add-group-btn" onClick={addGroup} className="flex items-center gap-1 px-3 py-1.5 text-[10px] uppercase tracking-widest font-bold bg-[#0A0A0A] text-white hover:bg-[#0020C2]"><Plus className="w-3.5 h-3.5" /> Ekle</button>
            </div>
          </div>

          {/* Page assignments */}
          <div>
            <div className="text-[11px] uppercase tracking-widest font-semibold text-neutral-600 mb-2">Sayfa Atamaları</div>
            <div className="border border-[#E5E5E5] divide-y divide-[#F5F5F5] max-h-[320px] overflow-y-auto">
              {MENU.map((m) => (
                <div key={m.to} className="flex items-center justify-between px-3 py-1.5 text-sm">
                  <span className={hidden.includes(m.to) ? "text-neutral-400 line-through" : ""}>{m.label}</span>
                  <select
                    data-testid={`menu-assign-${m.to.replace("/", "") || "dashboard"}`}
                    value={assignmentOf(m.to)}
                    onChange={(e) => assign(m.to, e.target.value)}
                    className="text-xs border border-[#E5E5E5] px-2 py-1 bg-white max-w-[180px]"
                  >
                    <option value="__none__">Grupsuz (üstte)</option>
                    {groups.map((g) => <option key={g.id} value={g.id}>{g.label}</option>)}
                    <option value="__hidden__">Gizle</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between px-6 py-4 border-t border-[#E5E5E5] sticky bottom-0 bg-white">
          <button data-testid="menu-reset-defaults" onClick={() => { setGroups(DEFAULT_GROUPS.map((g) => ({ ...g, items: [...g.items] }))); setHidden([]); }} className="flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">
            <RotateCcw className="w-3.5 h-3.5" /> Varsayılana Dön
          </button>
          <button data-testid="menu-editor-save" onClick={save} disabled={saving} className="flex items-center gap-1.5 px-4 py-2 text-[10px] uppercase tracking-widest font-bold bg-[#0020C2] text-white hover:bg-[#0A0A0A] disabled:opacity-50">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Kaydet
          </button>
        </div>
      </div>
    </div>
  );
};
